import React, { useEffect, useRef } from "react"

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement,
        options: {
          sitekey: string
          action?: string
          callback?: (token: string) => void
          "expired-callback"?: () => void
          "error-callback"?: () => void
        },
      ) => string
      remove: (widgetId: string) => void
      reset: (widgetId: string) => void
    }
  }
}

interface PublicCaptchaFieldProps {
  onTokenChange: (token: string | null) => void
  /** Must match server-side Turnstile verification (e.g. org_request_submit, newsletter_subscribe). */
  action?: string
  /** Increment this value to force widget reset to default state. */
  resetTrigger?: number
}

const TURNSTILE_SCRIPT_ID = "turnstile-script"

export const PublicCaptchaField: React.FC<PublicCaptchaFieldProps> = ({
  onTokenChange,
  action = "org_request_submit",
  resetTrigger = 0,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const widgetRef = useRef<string | null>(null)
  const siteKey = import.meta.env.VITE_CAPTCHA_SITE_KEY as string | undefined

  useEffect(() => {
    if (!siteKey || !containerRef.current) {
      onTokenChange(null)
      return
    }

    let existingScriptLoadHandler: (() => void) | null = null

    const loadAndRender = () => {
      if (!containerRef.current || !window.turnstile) return
      if (widgetRef.current) {
        window.turnstile.remove(widgetRef.current)
        widgetRef.current = null
      }
      widgetRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        action,
        callback: (token: string) => onTokenChange(token),
        "expired-callback": () => onTokenChange(null),
        "error-callback": () => onTokenChange(null),
      })
    }

    const existingScript = document.getElementById(
      TURNSTILE_SCRIPT_ID,
    ) as HTMLScriptElement | null

    if (!existingScript) {
      const script = document.createElement("script")
      script.id = TURNSTILE_SCRIPT_ID
      script.src =
        "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"
      script.async = true
      script.defer = true
      script.onload = loadAndRender
      document.head.appendChild(script)
    } else {
      if (window.turnstile) {
        loadAndRender()
      } else {
        // Script tag can exist before API is ready (multiple widgets mount quickly).
        // Subscribe once so this instance renders when Turnstile finishes loading.
        existingScriptLoadHandler = () => loadAndRender()
        existingScript.addEventListener("load", existingScriptLoadHandler, {
          once: true,
        })
      }
    }

    return () => {
      if (existingScript && existingScriptLoadHandler) {
        existingScript.removeEventListener("load", existingScriptLoadHandler)
      }
      if (widgetRef.current && window.turnstile) {
        window.turnstile.remove(widgetRef.current)
        widgetRef.current = null
      }
    }
  }, [siteKey, onTokenChange, action])

  useEffect(() => {
    if (!widgetRef.current || !window.turnstile) return
    window.turnstile.reset(widgetRef.current)
    onTokenChange(null)
  }, [resetTrigger, onTokenChange])

  if (!siteKey) {
    return (
      <div className="alert alert-warning py-2 small mb-0" role="alert">
        CAPTCHA is not configured for this environment.
      </div>
    )
  }

  return <div ref={containerRef} />
}

